"use client";

import { useContact } from "@/hooks/usePublicData";
import { useLocale } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import PaymentMethods from "@/components/ui/PaymentMethods";
import {
  FaFacebookF,
  FaGlobe,
  FaInstagram,
  FaLinkedinIn,
  FaSnapchat,
  FaTelegram,
  FaTiktok,
  FaWhatsapp,
  FaXTwitter,
  FaYoutube,
} from "react-icons/fa6";

function Icon({ name, className = "" }: { name: string; className?: string }) {
  return <span className={`material-symbols-outlined ${className}`}>{name}</span>;
}

const SOCIAL_ICONS: Record<string, React.ElementType> = {
  facebook: FaFacebookF, twitter: FaXTwitter, x: FaXTwitter, instagram: FaInstagram,
  tiktok: FaTiktok, youtube: FaYoutube, linkedin: FaLinkedinIn, snapchat: FaSnapchat,
  whatsapp: FaWhatsapp, telegram: FaTelegram, website: FaGlobe,
};

export default function Footer() {
  const locale = useLocale();
  const isAr = locale === "ar";
  const t = (a: string, e: string) => (isAr ? a : e);
  const { data: contact } = useContact();
  const year = new Date().getFullYear();

  const brand = t("منازل الدرعية", "Diriyah Homes");
  const exploreLinks = [
    { label: t("الوحدات", "Stays"), href: `/${locale}/explore` },
    { label: t("أضف وحدتك", "List your property"), href: `/${locale}/list-property` },
  ];
  const companyLinks = [
    { label: t("من نحن", "About us"), href: `/${locale}/about` },
    { label: t("المدوّنة", "Blog"), href: `/${locale}/blog` },
    { label: t("تواصل معنا", "Contact"), href: `/${locale}/contact` },
    { label: t("المساعدة", "Help center"), href: `/${locale}/help` },
    { label: t("الشروط والأحكام", "Terms & Conditions"), href: `/${locale}/terms` },
    { label: t("سياسة الخصوصية", "Privacy Policy"), href: `/${locale}/privacy` },
  ];

  const email = contact?.emails?.[0];
  const phone = contact?.phones?.[0]?.number;
  const address = contact?.address ? (isAr ? contact.address.ar : contact.address.en) : t("حي البجيري، الدرعية", "Al-Bujairi, Diriyah");

  return (
    <footer dir={isAr ? "rtl" : "ltr"} className="najdi relative overflow-hidden bg-[#14110f] text-white/60">
      <div className="h-px w-full bg-gradient-to-r from-transparent via-clay-600/60 to-transparent" />

      <div className="relative container mx-auto px-6 pb-10 pt-16">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-[2.2fr_1fr_1fr_1.2fr]">
          {/* Brand */}
          <div>
            <Link href={`/${locale}`} className="mb-5 inline-flex items-center">
              <Image src="/light-logo.png" alt={brand} width={150} height={48} className="h-11 w-auto object-contain" />
            </Link>
            <p className="max-w-xs text-sm leading-relaxed text-white/50">
              {t(
                "منصّة منازل الدرعية الرائدة للإقامات الفاخرة والتجارب والخدمات السياحية في أكثر المناطق تميزا بالسعودية.",
                "Diriyah Homes — the premier platform for luxury stays, experiences and tourism services in Saudi Arabia's most distinctive areas.",
              )}
            </p>
            {contact?.socials && contact.socials.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2.5">
                {contact.socials.map((s, i) => {
                  const SIcon = SOCIAL_ICONS[s.platform?.toLowerCase()] || FaGlobe;
                  return (
                    <a
                      key={i}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.platform}
                      className="flex w-10 h-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70 transition-all hover:bg-clay-600 hover:text-white"
                    >
                      <SIcon size={15} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Explore */}
          <div>
            <h4 className="mb-5 font-display text-lg font-bold text-clay-100">{t("استكشف", "Explore")}</h4>
            <ul className="space-y-3">
              {exploreLinks.map((l) => (
                <li key={l.href}>
                  <Link href={l.href} className="text-sm text-white/60 transition-colors hover:text-white">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="mb-5 font-display text-lg font-bold text-clay-100">{t("الشركة", "Company")}</h4>
            <ul className="space-y-3">
              {companyLinks.map((l) => (
                <li key={l.href}>
                  <Link href={l.href} className="text-sm text-white/60 transition-colors hover:text-white">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="mb-5 font-display text-lg font-bold text-clay-100">{t("تواصل معنا", "Contact")}</h4>
            <ul className="space-y-3 text-sm text-white/60">
              <li className="flex items-start gap-2">
                <Icon name="location_on" className="text-clay-100 text-[18px] shrink-0" />
                <span>{address}</span>
              </li>
              {phone && (
                <li className="flex items-center gap-2">
                  <Icon name="call" className="text-clay-100 text-[18px] shrink-0" />
                  <a href={`tel:${phone.replace(/\s/g, "")}`} dir="ltr" className="hover:text-white">{phone}</a>
                </li>
              )}
              {email && (
                <li className="flex items-center gap-2">
                  <Icon name="mail" className="text-clay-100 text-[18px] shrink-0" />
                  <a href={`mailto:${email}`} dir="ltr" className="hover:text-white break-all">{email}</a>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Accepted payment methods (content sheet) */}
        <div className="mt-12 flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
          <span className="text-xs font-bold text-white/60">{t("وسائل الدفع المتاحة", "We accept")}</span>
          <PaymentMethods size="sm" className="justify-center" />
        </div>

        {/* Bottom bar */}
        <div className="mt-6 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row">
          <span className="text-xs text-white/40">
            © {year} {brand}. {t("جميع الحقوق محفوظة.", "All rights reserved.")}
          </span>
          <div className="flex gap-5 text-xs text-white/40">
            <Link href={`/${locale}/terms`} className="transition-colors hover:text-white">{t("الشروط", "Terms")}</Link>
            <Link href={`/${locale}/privacy`} className="transition-colors hover:text-white">{t("الخصوصية", "Privacy")}</Link>
            <Link href={`/${locale}/help`} className="transition-colors hover:text-white">{t("المساعدة", "Help")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
